<template>
  <div class="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-4 hover:shadow-xl transition-shadow duration-300">
    <!-- Header with Interactive Elements -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center space-x-3">
        <!-- Device Icon with Animation -->
        <div class="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg shadow-lg">
          <svg class="w-5 h-5 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"/>
          </svg>
        </div>
        
        <div>
          <div class="flex items-center space-x-2">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ deviceDisplayName }} Preview
            </h3>
            <!-- Live Wallpaper Badge -->
            <div v-if="isLiveWallpaper" class="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm flex items-center space-x-1 shadow-sm">
              <span class="relative flex h-1.5 w-1.5">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span class="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
              </span>
              <span class="font-medium">LIVE</span>
            </div>
          </div>
          <div class="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
            <div class="flex items-center space-x-1">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <span>{{ dimensions.width }}×{{ dimensions.height }}</span>
            </div>
            <span>•</span>
            <div class="flex items-center space-x-1">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4a1 1 0 011-1h4m12 0h4a1 1 0 011 1v4m0 8v4a1 1 0 01-1 1h-4m-12 0H4a1 1 0 01-1-1v-4"/>
              </svg>
              <span>{{ aspectRatio }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Action Buttons -->
      <div class="flex items-center space-x-2">
        <!-- Fullscreen Button -->
        <button
          @click="$emit('fullscreen')"
          class="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300 hover:scale-110 active:scale-95 group"
        >
          <svg class="w-4 h-4 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </button>
        
        <!-- Share Button -->
        <button
          @click="$emit('share')"
          class="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-600 dark:hover:text-green-400 transition-all duration-300 hover:scale-110 active:scale-95 group"
        >
          <svg class="w-4 h-4 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Wallpaper Fit Options Dropdown (Desktop Only) -->
    <div class="mb-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700 hidden sm:block">
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center space-x-2">
          <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
            Choose how your wallpaper fits
          </div>
        </div>
        <div class="relative">
          <select
            v-model="selectedFit"
            @change="handleFitChange"
            class="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
          >
            <option value="fill">Fill - Stretch to fill screen</option>
            <option value="fit">Fit - Scale to fit within bounds</option>
            <option value="stretch">Stretch - Distort to exact size</option>
            <option value="tile">Tile - Repeat image pattern</option>
            <option value="center">Center - Center without scaling</option>
            <option value="span">Span - Extend across displays</option>
          </select>
          <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
      <div class="text-xs text-gray-500 dark:text-gray-400">
        {{ getFitDescription(selectedFit) }}
      </div>
    </div>
    
    <!-- Device Mockup Container with Swipe Support -->
    <div 
      class="relative flex justify-center items-center min-h-[400px] bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl overflow-hidden group cursor-pointer"
      @touchstart.passive="$emit('touch-start', $event)"
      @touchmove="$emit('touch-move', $event)"
      @touchend.passive="$emit('touch-end', $event)"
      @click="$emit('preview-click')"
    >
      <!-- Background Pattern -->
      <div class="absolute inset-0 opacity-10">
        <div class="absolute inset-0" style="background-image: radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0); background-size: 20px 20px;"></div>
      </div>
      
      <!-- Loading State -->
      <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
        <div class="flex flex-col items-center space-y-3">
          <div class="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p class="text-sm text-gray-500 dark:text-gray-400 animate-pulse">Loading preview...</p>
        </div>
      </div>
      
      <!-- Device Mockup -->
      <div v-else class="relative transform transition-all duration-500 group-hover:scale-105">
        <DeviceMockup 
          :device-type="deviceType" 
          :wallpaper="imageUrl"
          :video-url="isLiveWallpaper ? (videoUrl || imageUrl) : ''"
          :wallpaper-id="wallpaperId"
          :name="deviceDisplayName" 
          :show-label="false" 
          :clickable="true"
          :fit-option="selectedFit"
          class="drop-shadow-2xl"
        />
        
        <!-- Hover Overlay -->
        <div class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-end justify-center pb-4">
          <div class="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-900 dark:text-white">
            Click to view full size
          </div>
        </div>
      </div>
      
      <!-- Swipe Indicators for Mobile -->
      <div v-if="hasMultipleVariations" class="absolute bottom-4 left-4 right-4 sm:hidden">
        <div class="flex justify-center space-x-2">
          <div
            v-for="(_, index) in totalVariations"
            :key="index"
            :class="[
              'w-2 h-2 rounded-full transition-all duration-300',
              currentVariationIndex === index 
                ? 'bg-white shadow-lg' 
                : 'bg-white/50'
            ]"
          ></div>
        </div>
      </div>
    </div>
    
    <!-- Thumbnail Gallery Below Device (Mobile & Desktop) -->
    <div v-if="hasMultipleVariations && variations && variations.length > 1" class="mt-4">
      <div class="flex items-center justify-center space-x-2">
        <div
          v-for="(variation, index) in variations"
          :key="index"
          @click="$emit('select-variation', index)"
          :class="[
            'flex-shrink-0 rounded-lg transition-all duration-300 cursor-pointer relative overflow-hidden group border-2',
            currentVariationIndex === index 
              ? 'border-blue-500 shadow-lg scale-105' 
              : 'border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:scale-110'
          ]"
        >
          <!-- Thumbnail Image -->
          <div class="w-10 h-12 bg-gray-100 dark:bg-gray-700 rounded-md overflow-hidden">
            <SimpleSecureImage
              :image-url="variation.thumbnailUrl || variation.imageUrl"
              :wallpaper-id="wallpaperId"
              :is-preview="true"
              :alt="`${deviceDisplayName} Variation ${Number(index) + 1}`"
              :container-class="'w-full h-full'"
              :image-class="'w-full h-full object-cover transition-transform duration-300 group-hover:scale-110'"
              :target-width="120"
              :target-height="144"
            />
          </div>
          
          <!-- Active Indicator -->
          <div v-if="currentVariationIndex === index" 
               class="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent rounded-md">
            <div class="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
          </div>
          
          <!-- Hover Overlay -->
          <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 rounded-md flex items-center justify-center">
            <div class="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <svg class="w-3 h-3 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Device Info Footer -->
    <div class="mt-4 text-center">
      <div class="text-sm text-gray-600 dark:text-gray-300 font-medium">
        {{ dimensions.width }} × {{ dimensions.height }} • {{ aspectRatio }} aspect ratio
      </div>
      <div class="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center justify-center space-x-4">
        <div class="flex items-center space-x-1">
          <svg class="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          <span>Optimized for {{ deviceDisplayName }}</span>
        </div>
        
        <div v-if="hasMultipleVariations" class="flex items-center space-x-1">
          <svg class="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          <span class="hidden sm:inline">Use arrow keys to navigate</span>
          <span class="sm:hidden">Swipe to navigate</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { DeviceMockup } from '../../components/ui'
import SimpleSecureImage from '../ui/SimpleSecureImage.vue'
import { isLikelyVideoUrl } from '../../utils/validation'

interface Variation {
  id: string
  imageUrl: string
  thumbnailUrl?: string
  dimensions: {
    width: number
    height: number
  }
  validationScore?: number
  isPrimary: boolean
}

interface Props {
  deviceType: string
  deviceDisplayName: string
  imageUrl: string
  wallpaperId: string // Added wallpaperId for security
  dimensions: { width: number; height: number }
  aspectRatio: string
  hasMultipleVariations: boolean
  isLoading?: boolean
  variations?: Variation[]
  currentVariationIndex?: number
  totalVariations?: number
  fitOption?: string
  // Live wallpaper props
  isLiveWallpaper?: boolean
  videoUrl?: string
  isVideo?: boolean
  isAnimated?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'fullscreen': []
  'share': []
  'preview-click': []
  'touch-start': [event: TouchEvent]
  'touch-move': [event: TouchEvent]
  'touch-end': [event: TouchEvent]
  'select-variation': [index: number]
  'fit-changed': [fitOption: string]
}>()

// Fit options state
const selectedFit = ref(props.fitOption || 'fill')

// Handle fit option change
const handleFitChange = () => {
  emit('fit-changed', selectedFit.value)
  // Save to localStorage
  localStorage.setItem('wallpaper-fit-preference', selectedFit.value)
}

// Load saved fit preference on mount
onMounted(() => {
  const savedFit = localStorage.getItem('wallpaper-fit-preference')
  if (savedFit && !props.fitOption) {
    selectedFit.value = savedFit
  } else if (props.fitOption) {
    selectedFit.value = props.fitOption
  }
})

// Get fit description for better user understanding
const getFitDescription = (fitOption: string) => {
  const descriptions = {
    fill: 'Image will stretch to completely fill the screen, may crop parts of the image',
    fit: 'Image will scale to fit entirely within the screen, maintaining aspect ratio',
    stretch: 'Image will be distorted to match exact screen dimensions',
    tile: 'Image will repeat in a pattern to fill the entire screen',
    center: 'Image will be centered at original size without scaling',
    span: 'Image will extend across multiple displays as one continuous image'
  }
  return descriptions[fitOption as keyof typeof descriptions] || descriptions.fill
}

// Live wallpaper detection
const isLiveWallpaper = computed(() => {
  // Check explicit flags
  if (props.isVideo || props.isAnimated) return true
  if (props.videoUrl && props.videoUrl.trim().length > 0) return true
  // Also check the imageUrl if it's a video
  return isLikelyVideoUrl(props.imageUrl)
})

</script>

<style scoped>
/* Custom animations and effects */
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

/* Glassmorphism effect */
.backdrop-blur-sm {
  backdrop-filter: blur(4px);
}

/* Custom hover effects */
.group:hover .drop-shadow-2xl {
  filter: drop-shadow(0 25px 25px rgb(0 0 0 / 0.15));
}
</style>